<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps({
  error: Object as () => NuxtError,
})

const handleError = () => clearError({ redirect: '/' })

const errorTitle = computed(() => {
  switch (props.error?.statusCode) {
    case 404:
      return 'Page Not Found'
    case 500:
      return 'Server Error'
    default:
      return 'An Error Occurred'
  }
})

const errorDescription = computed(() => {
  if (props.error?.statusMessage) {
    return props.error.statusMessage
  }
  
  switch (props.error?.statusCode) {
    case 404:
      return 'The page you are looking for does not exist or has been moved.'
    case 500:
      return 'Something went wrong on our end. Please try again later.'
    default:
      return 'An unexpected error occurred. Please try again.'
  }
})
</script>

<template>
  <UApp>
    <UContainer class="flex items-center justify-center min-h-screen py-12">
      <UCard class="max-w-2xl w-full">
        <div class="text-center space-y-6">
          <!-- Error Code -->
          <div class="text-primary text-8xl font-bold">
            {{ error?.statusCode || 500 }}
          </div>

          <!-- Error Title -->
          <div>
            <h1 class="text-3xl font-bold mb-2">
              {{ errorTitle }}
            </h1>
            <p class="text-muted-foreground text-lg">
              {{ errorDescription }}
            </p>
          </div>

          <!-- Error Details (only in development) -->
          <div v-if="error?.message && $config.public.dev" class="text-left">
            <UAlert
              color="error"
              variant="subtle"
              :title="error.message"
            />
          </div>

          <!-- Actions -->
          <div class="flex gap-3 justify-center pt-4">
            <UButton
              size="lg"
              @click="handleError"
            >
              <UIcon name="i-heroicons-home" class="w-5 h-5" />
              Go to Home
            </UButton>
            
            <UButton
              size="lg"
              color="neutral"
              variant="outline"
              @click="$router.back()"
            >
              <UIcon name="i-heroicons-arrow-left" class="w-5 h-5" />
              Go Back
            </UButton>
          </div>
        </div>
      </UCard>
    </UContainer>
  </UApp>
</template>
